<template>
	<div>
		<div v-if="title" class="px-4 pt-3 pb-1 text-subtitle-1 font-weight-medium">{{ title }}</div>
		<div ref="chartContainer" style="width: 100%; height: 520px;"></div>
	</div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import am4themes_animated from '@amcharts/amcharts4/themes/animated'

am4core.useTheme(am4themes_animated)

const MAX_LABEL_CHARS = 28

function truncateName(name) {
	if (name == null) return '-'
	const s = String(name).replace(/\s+/g, ' ').trim()
	return s.length > MAX_LABEL_CHARS ? s.slice(0, MAX_LABEL_CHARS - 1).trimEnd() + '…' : s
}

export default {
	name: 'top-groups-chart',

	props: {
		items: {
			type: Array,
			default: () => [],
		},
		topN: {
			type: Number,
			default: 10,
		},
		seriesALabel: {
			type: String,
			default: 'Periodo A',
		},
		seriesBLabel: {
			type: String,
			default: 'Periodo B',
		},
		title: {
			type: String,
			default: '',
		},
		colorA: {
			type: String,
			default: '#1976D2',
		},
		colorB: {
			type: String,
			default: '#90CAF9',
		},
	},

	data() {
		return {
			chart: null,
		}
	},

	mounted() {
		this.$nextTick(() => this.initialize())
	},

	beforeDestroy() {
		if (this.chart) {
			this.chart.dispose()
		}
	},

	watch: {
		items: {
			handler() {
				this.initialize()
			},
			deep: true,
		},
		topN() {
			this.initialize()
		},
	},

	methods: {
		initialize() {
			if (this.chart) {
				this.chart.dispose()
				this.chart = null
			}

			if (!this.$refs.chartContainer) {
				return
			}

			if (!Array.isArray(this.items) || this.items.length === 0) {
				return
			}

			// Sort by totalrow desc, keep only items with numeric totalrow > 0, take top N
			const sorted = [...this.items]
				.filter(i => i && (parseFloat(i.totalrow) || 0) > 0)
				.sort((a, b) => (parseFloat(b.totalrow) || 0) - (parseFloat(a.totalrow) || 0))
				.slice(0, this.topN)

			if (sorted.length === 0) {
				return
			}

			// Pre-truncate names to fit in the axis label area. Keep full name for tooltip.
			const data = sorted.map((item) => {
				const fullName = item.name || item.code || '-'
				return {
					name: truncateName(fullName),
					fullName,
					code: item.code || '',
					valueA: parseFloat(item.totalrow) || 0,
					valueB: parseFloat(item.totalrow_1) || 0,
				}
			})

			const chart = am4core.create(this.$refs.chartContainer, am4charts.XYChart)
			chart.data = data
			chart.fontFamily = "'Roboto', sans-serif"
			chart.fontSize = 11
			chart.paddingRight = 30
			chart.paddingLeft = 10
			chart.numberFormatter.numberFormat = '#,###.##'
			chart.language.locale = am4core.language ? am4core.language.locale : chart.language.locale

			// Category axis: vertical (names on Y)
			const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis())
			categoryAxis.dataFields.category = 'name'
			categoryAxis.renderer.grid.template.location = 0
			categoryAxis.renderer.minGridDistance = 1
			categoryAxis.renderer.inversed = true
			categoryAxis.renderer.cellStartLocation = 0.15
			categoryAxis.renderer.cellEndLocation = 0.85
			categoryAxis.renderer.labels.template.fontSize = 11
			// Limit the total width the Y axis can take so the chart area stays usable
			categoryAxis.renderer.minWidth = 180
			categoryAxis.renderer.maxWidth = 200
			categoryAxis.renderer.labels.template.maxWidth = 190
			categoryAxis.renderer.labels.template.wrap = false
			categoryAxis.renderer.labels.template.truncate = true
			// Disable the automatic "full text" tooltip that amcharts shows on truncated labels —
			// otherwise it would overlap with the two series tooltips.
			categoryAxis.renderer.labels.template.tooltipText = ''
			categoryAxis.cursorTooltipEnabled = false
			// Extra safety: adapter that re-truncates if amcharts bypasses the maxWidth
			categoryAxis.renderer.labels.template.adapter.add('text', (text) => {
				if (text == null) return text
				return String(text).length > MAX_LABEL_CHARS
					? String(text).slice(0, MAX_LABEL_CHARS - 1).trimEnd() + '…'
					: text
			})

			// Value axis: horizontal (amount in €)
			const valueAxis = chart.xAxes.push(new am4charts.ValueAxis())
			valueAxis.renderer.opposite = false
			valueAxis.min = 0
			valueAxis.renderer.labels.template.fontSize = 10
			valueAxis.cursorTooltipEnabled = false
			valueAxis.renderer.labels.template.adapter.add('text', (text) => {
				if (text == null || text === '') return text
				return text + ' €'
			})

			// Shared bar styling
			const barHeight = am4core.percent(90)

			// Unified tooltip text: shows fullName + both periods in one popup,
			// used for the hovered series. Only one of A/B can be under the mouse at a time,
			// so there's never a second tooltip overlapping this one.
			const unifiedTooltip =
				"[bold]{fullName}[/]\n" +
				this.seriesALabel + ": {valueA.formatNumber('#,###.00')} €\n" +
				this.seriesBLabel + ": {valueB.formatNumber('#,###.00')} €"

			// Series A
			const seriesA = chart.series.push(new am4charts.ColumnSeries())
			seriesA.name = this.seriesALabel
			seriesA.dataFields.valueX = 'valueA'
			seriesA.dataFields.categoryY = 'name'
			seriesA.columns.template.height = barHeight
			seriesA.columns.template.strokeWidth = 0
			seriesA.fill = am4core.color(this.colorA)
			seriesA.stroke = am4core.color(this.colorA)
			seriesA.sequencedInterpolation = true
			seriesA.columns.template.tooltipText = unifiedTooltip
			if (seriesA.tooltip) {
				seriesA.tooltip.pointerOrientation = 'left'
				seriesA.tooltip.label.maxWidth = 320
				seriesA.tooltip.label.wrap = true
				seriesA.tooltip.label.textAlign = 'start'
			}

			// Series B
			const seriesB = chart.series.push(new am4charts.ColumnSeries())
			seriesB.name = this.seriesBLabel
			seriesB.dataFields.valueX = 'valueB'
			seriesB.dataFields.categoryY = 'name'
			seriesB.columns.template.height = barHeight
			seriesB.columns.template.strokeWidth = 0
			seriesB.fill = am4core.color(this.colorB)
			seriesB.stroke = am4core.color(this.colorB)
			seriesB.sequencedInterpolation = true
			seriesB.columns.template.tooltipText = unifiedTooltip
			if (seriesB.tooltip) {
				seriesB.tooltip.pointerOrientation = 'left'
				seriesB.tooltip.label.maxWidth = 320
				seriesB.tooltip.label.wrap = true
				seriesB.tooltip.label.textAlign = 'start'
			}

			// No XYCursor: avoids the "show tooltip for every series on the same category" behavior
			// that would stack A + B tooltips on top of each other.

			// Legend: simple, two entries (series A / B)
			chart.legend = new am4charts.Legend()
			chart.legend.contentAlign = 'center'
			chart.legend.fontSize = 11
			chart.legend.labels.template.truncate = false

			this.chart = chart
		},
	},
}
</script>

<style scoped>
</style>
